import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
// Base styles for the phone input library + our wrapper overrides.
// Loaded once here so the PhoneInputField component can be dropped
// into any form without each consumer importing CSS.
import 'react-phone-number-input/style.css'
import './components/ui/phone-input.css'
// Tracks open/closed state on every native <select> so the global
// chevron-rotation CSS in index.css can drive off `data-open`
// instead of `:focus`. Self-installs on import.
import './lib/dropdownArrowGlue'
import { registerServiceWorker } from './lib/pwa'
import { ErrorBoundary } from './components/ErrorBoundary'
import { isChunkLoadFailure, recoverFromStaleBundle } from './lib/stale-bundle'
import { initDisplayPrefs } from './lib/display-prefs'
import { build, buildLabel } from './lib/env'
import App from './App'

// Paint the last-applied display & accessibility preferences (theme, text
// size, motion, contrast, link underlines) onto <html> BEFORE React renders,
// so there's no light-then-dark flash on first paint. The signed-in user's
// account values replace them as soon as /me/preferences answers.
initDisplayPrefs()

// Machine-readable build stamp. QA-017: nothing in the running app said which
// build it was, so a tester could not confirm the build under test. A meta tag
// is readable without a session and without opening the bundle.
{
  const meta = document.createElement('meta')
  meta.name = 'app-build'
  meta.content = buildLabel()
  document.head.appendChild(meta)
  if (build.builtAt) {
    const at = document.createElement('meta')
    at.name = 'app-built-at'
    at.content = build.builtAt
    document.head.appendChild(at)
  }
}

// ─── Stale-bundle auto-recovery ─────────────────────────────────
//
// A tab loaded BEFORE a deploy is still running the previous build.
// The first time it touches a chunk the new server no longer has it
// throws — a failed dynamic import surfaces here as a global `error` /
// `unhandledrejection`, and the shared helper does a one-time reload to
// pull the fresh index.html. React RENDER crashes from the same cause
// (the infamous #130 "element type … undefined") do NOT reach these
// listeners — they are caught by <ErrorBoundary> around <App/> below,
// which routes through the same recovery. Helpers live in
// `lib/stale-bundle.js` so both entry points share one cooldown.
window.addEventListener('error', (event) => {
  if (isChunkLoadFailure(event.message)) recoverFromStaleBundle(event.message)
})
window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message ?? event.reason
  if (isChunkLoadFailure(msg)) recoverFromStaleBundle(msg)
})

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)

// Register the PWA service worker AFTER React mounts — keeps the
// SW lifecycle from interfering with the initial render. Safe to
// call in dev (it's a no-op there per the implementation).
void registerServiceWorker()
